import React from "react";
import { Icon } from "@iconify/react";

/**
 * One drawer of the Settings console.
 *
 * THE HEADING IS THE POINT. Every section title on this page used to be
 * `<p className="fw-bold mb-1">`, one weight step from the `fw-semibold`
 * labels beneath it, which is exactly why nothing read as a section. A real
 * `<h5 className="fw-semibold">` inside a real `.card-header` is what picks up
 * Sora and the brand letter-spacing from the skin in App.css. Changing the
 * element is what buys the visual upgrade; no new CSS is needed for the type.
 *
 * The whole header is a <button>, so a keyboard operator tabs seven stops
 * rather than hunting a 16px chevron, and on a phone the hit target is the
 * full row. No Bootstrap accordion JS and no react-bootstrap: the body is
 * conditionally rendered from React state, which keeps the open set knowable
 * for the save bar.
 *
 * The icon tile is a soft violet rounded SQUARE with a hairline border, not a
 * gradient disc with a glow. That restraint is the house style on
 * curiouzz.com and the thing a first pass usually gets wrong.
 */
const SettingsSection = ({
  id,
  icon,
  title,
  summary,
  open,
  onToggle,
  editable = true,
  dirtyCount = 0,
  children,
}) => (
  <div className="card mt-24" data-section={id}>
    <button
      type="button"
      className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center gap-3 w-100 text-start"
      style={{ background: "transparent", border: 0, cursor: "pointer" }}
      aria-expanded={open}
      aria-controls={`section-body-${id}`}
      onClick={onToggle}
    >
      <span
        className="w-40-px h-40-px radius-8 d-flex justify-content-center align-items-center flex-shrink-0"
        style={{
          background: "rgba(140, 80, 253, 0.08)",
          border: "1px solid rgba(140, 80, 253, 0.18)",
        }}
        aria-hidden="true"
      >
        <Icon icon={icon} style={{ fontSize: "1.125rem", color: "var(--cz-purple, #8c50fd)" }} />
      </span>

      <span className="flex-grow-1" style={{ minWidth: 0 }}>
        <h5 className="fw-semibold mb-0">{title}</h5>
        <span
          className="text-sm text-secondary-light d-block text-truncate"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {summary}
        </span>
      </span>

      {dirtyCount > 0 && (
        <span className="bg-warning-100 text-warning-600 px-8 py-2 radius-4 text-xs fw-semibold flex-shrink-0">
          {dirtyCount} unsaved
        </span>
      )}

      {/* On a view-only account the pill sits on the CLOSED header, so nobody
          opens seven drawers before discovering they cannot change anything.
          The old page simply removed every Save button and said nothing. */}
      {!editable && (
        <span className="bg-neutral-200 text-secondary-light px-8 py-2 radius-4 text-xs fw-semibold flex-shrink-0">
          View only
        </span>
      )}

      <Icon
        icon={open ? "iconamoon:arrow-up-2" : "iconamoon:arrow-down-2"}
        className="text-secondary-light flex-shrink-0"
        style={{ fontSize: "1.25rem" }}
        aria-hidden="true"
      />
    </button>

    {open && (
      <div className="card-body" id={`section-body-${id}`}>
        {children}
      </div>
    )}
  </div>
);

export default SettingsSection;
